import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableContainer,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  StyledTable,
  StyledTableCard,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow,
} from "@/components/styled";
import TablePagination from "@/components/TablePagination";
import CommonTableActions from "@/components/CommonTableActions";
import TableFilter from "@/components/TableFilter";
import TableSort from "@/components/TableSort";
import SVGExport from "@/assets/icons/SVGExport";
import { COMMON_SORT_ITEMS } from "@/constants";
import { getFinanceReviewProjects } from "@/api/financeApi";
import { updateProject } from "@/api/projectsApi";
import useTablePagination from "@/hooks/useTablePagination";
import useDebouncedSearch from "@/hooks/useDebouncedSearch";
import useTableSort from "@/hooks/useTableSort";
import useTableFilter from "@/hooks/useTableFilter";
import { downloadCSV } from "@/utils/downloadCSV";
import { FinanceRow } from "@/types";
import {
  financeTableHeaders,
  FinanceTableHeader,
} from "@/utils/financeTableHeaders";
import { mapApiToRow } from "@/utils/projectUtils";
import SVGSort from "@/assets/icons/SVGSort";
import FinanceTableBody from "./FinanceTableBody";
import axios from "@/libs/axios";

const STATUS_FILTER_OPTIONS = [
  { value: "approved", label: "Approved" },
  {
    value: "verification_with_contractor_needed",
    label: "Verification with contractor needed",
  },
  { value: "reviewed_and_ready", label: "Reviewed and ready" },
  { value: "final_review_scheduled", label: "Finance Review" },
];

const FinanceTable = () => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState<FinanceRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [snackbarError, setSnackbarError] = useState<string | null>(null);
  const [snackbarSuccess, setSnackbarSuccess] = useState<string | null>(null);

  const { page, rowsPerPage, handleChangeRowsPerPage, handleChangePage } =
    useTablePagination(rows, totalCount);
  const { searchValue, handleDebounceSearchValue } =
    useDebouncedSearch(handleChangePage);
  const { sort, handleSortChange } = useTableSort();
  const { filters, handleFilterChange } = useTableFilter(handleChangePage);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchValue,
        filter: filters,
        sort: sort,
      };
      const response = await getFinanceReviewProjects(params);
      const data = response?.data?.data || [];
      const mappedRows = data.map(mapApiToRow);
      setRows(mappedRows);
      setTotalCount(response?.data?.metadata?.total || data.length);
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : "Unknown error"
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, filters, sort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectRow = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };
  const isSelected = (id: number) => selected.includes(id);

  const handleExport = () => {
    const exportRows =
      selected.length > 0
        ? rows.filter((row) => selected.includes(row.id))
        : rows;
    downloadCSV(
      exportRows,
      "finance",
      financeTableHeaders.map((header: FinanceTableHeader) => header.label)
    );
  };

  const handleSaveNote = async (rowId: number, note: string) => {
    try {
      const response = await axios.patch(`/project/update/${rowId}`, {
        finance_notes: note,
      });
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === rowId ? { ...row, note } : row))
      );
      setSnackbarSuccess("Note updated successfully!");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update note";
      setSnackbarError(errorMessage);
    }
  };

  return (
    <StyledTableCard sx={{ width: "100%" }}>
      <CommonTableActions
        title={"Finance"}
        onSearch={handleDebounceSearchValue}
        sort={
          <TableSort
            placeholder={"Sort"}
            items={COMMON_SORT_ITEMS}
            setSortValue={handleSortChange}
          />
        }
        filter={
          <TableFilter
            placeholder={"All statuses"}
            items={STATUS_FILTER_OPTIONS}
            setFilterValues={handleFilterChange}
            filterValues={filters}
          />
        }
        rightActions={
          <Button
            sx={{
              borderRadius: "12px",
              px: 2.5,
              py: 1.25,
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              boxShadow: "none",
              whiteSpace: "nowrap",
            }}
            onClick={handleExport}
            startIcon={<SVGExport color="white" />}
            variant="contained"
            color="primary"
            disabled={rows.length === 0}
          >
            {selected.length > 0
              ? `Export to CSV (${selected.length})`
              : "Export to CSV"}
          </Button>
        }
      />
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ color: "red", textAlign: "center", minHeight: 200 }}>
          Error loading data
        </Box>
      ) : (
        <TableContainer>
          <StyledTable aria-label="finance table">
            <StyledTableHeader>
              <TableRow>
                <StyledTableCell padding="checkbox" />
                {financeTableHeaders.map((header: FinanceTableHeader) => {
                  const isSorted =
                    sort === `${header.key}Asc` || sort === `${header.key}Desc`;
                  const isAsc = sort === `${header.key}Asc`;
                  return (
                    <StyledTableCell
                      key={header.key as string}
                      sx={{
                        cursor: header.sortable ? "pointer" : "default",
                        userSelect: "none",
                        fontWeight: isSorted ? 700 : 500,
                        color: isSorted ? "#0076CE" : undefined,
                        padding: "4px 8px",
                      }}
                      onClick={
                        header.sortable
                          ? () => {
                              if (isSorted) {
                                handleSortChange(
                                  `${header.key}${isAsc ? "Desc" : "Asc"}`
                                );
                              } else {
                                handleSortChange(`${header.key}Asc`);
                              }
                            }
                          : undefined
                      }
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {header.label}
                        {header.sortable && (
                          <span
                            style={{
                              opacity: isSorted ? 1 : 0.4,
                              display: "inline-flex",
                              transform:
                                isSorted && !isAsc
                                  ? "rotate(180deg)"
                                  : undefined,
                            }}
                          >
                            <SVGSort />
                          </span>
                        )}
                      </Box>
                    </StyledTableCell>
                  );
                })}
                {/* Removed Actions column */}
              </TableRow>
            </StyledTableHeader>
            <FinanceTableBody
              filteredRows={rows}
              financeTableHeaders={financeTableHeaders}
              isSelected={isSelected}
              handleSelectRow={handleSelectRow}
              handleSaveNote={handleSaveNote}
            />
          </StyledTable>
        </TableContainer>
      )}
      <TablePagination
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalCount={totalCount}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 1000 }}
        open={!!snackbarError}
        autoHideDuration={6000}
        onClose={() => setSnackbarError(null)}
      >
        <Alert onClose={() => setSnackbarError(null)} severity="error">
          {snackbarError}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 1000 }}
        open={!!snackbarSuccess}
        autoHideDuration={6000}
        onClose={() => setSnackbarSuccess(null)}
      >
        <Alert onClose={() => setSnackbarSuccess(null)} severity="success">
          {snackbarSuccess}
        </Alert>
      </Snackbar>
    </StyledTableCard>
  );
};

export default FinanceTable;
